// ReportConfig.js
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';

// Initial report columns configuration
export const initialReportColumns = [
  { id: 'roleId', label: 'Role ID', visible: true },
  { id: 'jobId', label: 'Job ID', visible: true },
  { id: 'systemId', label: 'System ID', visible: true },
  { id: 'role', label: 'Role Name', visible: true },
  { id: 'roleType', label: 'Role Type', visible: true },
  { id: 'client', label: 'Client', visible: true },
  { id: 'clientPOC', label: 'Client POC', visible: true },
  { id: 'location', label: 'Location', visible: true },
  { id: 'roleLocation', label: 'Work Mode', visible: true },
  { id: 'experience', label: 'Experience', visible: true },
  { id: 'urgency', label: 'Urgency', visible: true },
  { id: 'status', label: 'Role Status', visible: true },
  { id: 'assignTo', label: 'Role Owner', visible: true },
  { id: 'currency', label: 'Currency', visible: true },
  { id: 'minRate', label: 'Min Rate', visible: true },
  { id: 'maxRate', label: 'Max Rate', visible: true },
  { id: 'startDate', label: 'Start Date', visible: true },
  { id: 'endDate', label: 'End Date', visible: true },
  { id: 'profilesNeeded', label: 'Profiles Needed', visible: true },
  { id: 'createdAt', label: 'Role Created At', visible: true },
  { id: 'createdBy', label: 'Created By', visible: true },
  { id: 'visaTypes', label: 'Visa Types', visible: true },
  { id: 'gbamsId', label: 'GBAMS ID', visible: true },
  { id: 'totalApplications', label: 'Total Applications', visible: true },
  { id: 'appliedCount', label: 'Applied Count', visible: true },
  { id: 'hiredCount', label: 'Hired Count', visible: true },
  { id: 'interviewsCount', label: 'Interviews Count', visible: true },
  { id: 'assignedRecruiters', label: 'Assigned Recruiters', visible: true },
  { id: 'applicationId', label: 'Application ID', visible: true },
  { id: 'candidateName', label: 'Candidate Name', visible: true },
  { id: 'candidateEmail', label: 'Candidate Email', visible: true },
  { id: 'candidatePhone', label: 'Candidate Phone', visible: true },
  { id: 'candidateExperience', label: 'Candidate Experience', visible: true },
  { id: 'currentCompany', label: 'Current Company', visible: true },
  { id: 'expectedSalary', label: 'Expected Salary', visible: true },
  { id: 'candidateLocation', label: 'Candidate Location', visible: true },
  { id: 'applicationStatus', label: 'Application Status', visible: true },
  { id: 'appliedAt', label: 'Applied At', visible: true },
  { id: 'submittedBy', label: 'Submitted By', visible: true },
  { id: 'resumeCount', label: 'Resume Count', visible: true }
];

// Custom hook for report functions
export const useReportFunctions = () => {
  const [showReportColumnModal, setShowReportColumnModal] = useState(false);
  const [selectedReportPeriod, setSelectedReportPeriod] = useState('');
  const [reportColumns, setReportColumns] = useState(initialReportColumns);

  const openReportColumnModal = useCallback((period) => {
    setSelectedReportPeriod(period);
    // Reset all columns to visible when opening the modal
    setReportColumns(prev => prev.map(col => ({ ...col, visible: true })));
    setShowReportColumnModal(true);
  }, []);

  const closeReportColumnModal = useCallback(() => {
    setShowReportColumnModal(false);
    setSelectedReportPeriod('');
  }, []);

  const toggleReportColumn = useCallback((columnId) => {
    setReportColumns(prev =>
      prev.map(col =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  }, []);

  const selectAllColumns = useCallback(() => {
    setReportColumns(prev => prev.map(col => ({ ...col, visible: true })));
  }, []);

  const deselectAllColumns = useCallback(() => {
    setReportColumns(prev => prev.map(col => ({ ...col, visible: false })));
  }, []);

  const downloadRolesReportWithColumns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      // Get selected column IDs
      const selectedColumnIds = reportColumns
        .filter(col => col.visible)
        .map(col => col.id);

      // Build URL with query parameters
      const params = new URLSearchParams();
      params.append('period', selectedReportPeriod);
      selectedColumnIds.forEach(colId => params.append('columns', colId));

      const apiUrl = `${BASE_URL}/api/recruitment/roles/export/report?${params.toString()}`;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });

      // Create blob link to download
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;

      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'recruitment_report.csv';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      // Show success notification and close modal
      closeReportColumnModal();
      
      return { success: true, message: 'Report downloaded successfully!' };

    } catch (error) {
      console.error('Error downloading report:', error);
      let errorMessage = 'Failed to download report';

      if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to download reports';
      } else if (error.response?.status === 404) {
        errorMessage = 'Report endpoint not found. Check your API configuration.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return { success: false, message: errorMessage };
    }
  }, [reportColumns, selectedReportPeriod, closeReportColumnModal]);

  return {
    showReportColumnModal,
    selectedReportPeriod,
    reportColumns,
    setReportColumns,
    openReportColumnModal,
    closeReportColumnModal,
    toggleReportColumn,
    selectAllColumns,
    deselectAllColumns,
    downloadRolesReportWithColumns
  };
};

// Report Column Modal Component
export const ReportColumnModal = ({
  showModal,
  onClose,
  selectedPeriod,
  reportColumns,
  onToggleColumn,
  onSelectAll,
  onDeselectAll,
  onDownload,
  showNotification
}) => {
  if (!showModal) return null;

  const handleDownload = async () => {
    const result = await onDownload();
    if (showNotification && result.message) {
      showNotification(result.message);
    }
  };

  return (
    <div
      id="report-column-modal"
      className="recruitment-modal"
      onClick={(e) => {
        if (e.target.id === 'report-column-modal') {
          if (window.confirm('Close without saving?')) {
            onClose();
          }
        }
      }}
    >
      <div className="recruitment-modal-content">
        <div className="recruitment-modal-header">
          <h2>Select Columns for {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)} Report</h2>
          <span className="recruitment-close" onClick={onClose}>&times;</span>
        </div>
        <div className="recruitment-modal-body">
          {/* Static Header with Select All/Deselect All */}
          <div className="column-controls">
            <div className="column-controls-buttons">
              <button
                type="button"
                className="recruitment-submit-btn"
                onClick={onSelectAll}
              >
                Select All
              </button>
              <button
                type="button"
                className="recruitment-cancel-btn"
                onClick={onDeselectAll}
              >
                Deselect All
              </button>
            </div>
          </div>

          {/* Scrollable Columns Grid */}
          <div className="columns-grid-container">
            <div className="columns-grid">
              {reportColumns.map((column) => (
                <div key={column.id} className="column-item">
                  <input
                    type="checkbox"
                    id={`column-${column.id}`}
                    checked={column.visible}
                    onChange={() => onToggleColumn(column.id)}
                  />
                  <label htmlFor={`column-${column.id}`}>
                    {column.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Static Footer with Download and Cancel */}
          <div className="recruitment-form-actions">
            <button
              type="button"
              className="recruitment-cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="recruitment-submit-btn"
              onClick={handleDownload}
              disabled={!reportColumns.some(col => col.visible)}
            >
              Download Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default {
  useReportFunctions,
  ReportColumnModal,
  initialReportColumns
};